// src/hooks/useLearningTip.ts

import { useEffect, useRef, useCallback } from "react";
import { FEATURE_FLAGS } from "../config/featureFlags";
import { getAvailableTips } from "../config/learningTips";
import { useSession } from "../contexts/SessionContext";
import {
  UseLearningTipResult,
  UseLearningTipOptions,
  LearningTip,
} from "../interfaces/learningTipTypes";
import { createLogger } from "../utils/logger";

const log = createLogger("useLearningTip");

/**
 * Probability of showing a learning tip on each rotation.
 * 40% = 0.4 (tips shown ~40% of rotations, words shown ~60%)
 */
const TIP_PROBABILITY = 0.4;

/**
 * Hook for managing learning tips during the AI thinking phase.
 *
 * DESIGN (Per-Rotation Probability):
 * - Each rotation independently decides whether to show a tip or word
 * - `shouldShowTip()` does a probability check (40% chance)
 * - `getNextTip()` returns the next tip content (tracks seen tips)
 * - Tips rotate without repetition within session until all shown
 *
 * Usage in ThinkingSection:
 * ```
 * const { shouldShowTip, getNextTip } = useLearningTip({ agentId, isThinking });
 *
 * // Each rotation:
 * if (shouldShowTip()) {
 *   const tip = getNextTip();
 *   // show tip
 * } else {
 *   // show thinking word
 * }
 * ```
 */
export function useLearningTip({
  agentId,
  isThinking,
}: UseLearningTipOptions): UseLearningTipResult {
  const { markTipAsSeen, getSeenTipIds, resetSeenTips } = useSession();

  // Cache available tips for the current agent
  const availableTipsRef = useRef<LearningTip[]>([]);

  // Load tips when thinking starts, clear when thinking stops
  useEffect(() => {
    if (isThinking && FEATURE_FLAGS.ENABLE_LEARNING_TIPS) {
      availableTipsRef.current = getAvailableTips(agentId);
      log.debug("Loaded tips for agent", {
        agentId,
        tipCount: availableTipsRef.current.length,
      });
    } else {
      // Clear tips when thinking stops
      availableTipsRef.current = [];
    }
  }, [isThinking, agentId]);

  /**
   * Select a tip from available tips, handling seen/unseen logic.
   * Picks from unseen tips first, resets when all have been shown.
   */
  const selectTip = useCallback((): LearningTip | null => {
    const availableTips = availableTipsRef.current;
    if (availableTips.length === 0) {
      return null;
    }

    const seenTipIds = getSeenTipIds();
    const unseenTips = availableTips.filter((tip) => !seenTipIds.has(tip.id));

    let selectedTip: LearningTip;

    if (unseenTips.length > 0) {
      // Select random from unseen tips
      selectedTip = unseenTips[Math.floor(Math.random() * unseenTips.length)];
      log.debug("Selected unseen tip", {
        tipId: selectedTip.id,
        agentId,
        unseenCount: unseenTips.length,
        totalTips: availableTips.length,
      });
    } else {
      // All tips seen - reset and pick random
      resetSeenTips();
      selectedTip = availableTips[Math.floor(Math.random() * availableTips.length)];
      log.info("All tips seen, reset cycle", {
        tipId: selectedTip.id,
        agentId,
        totalTips: availableTips.length,
      });
    }

    // Mark as seen
    markTipAsSeen(selectedTip.id);

    return selectedTip;
  }, [agentId, getSeenTipIds, resetSeenTips, markTipAsSeen]);

  /**
   * Check if we should show a tip this rotation.
   * Does a probability check each time it's called.
   * Returns false if feature is disabled or no tips available.
   */
  const shouldShowTip = useCallback((): boolean => {
    if (!FEATURE_FLAGS.ENABLE_LEARNING_TIPS) {
      return false;
    }

    if (availableTipsRef.current.length === 0) {
      return false;
    }

    const roll = Math.random();
    const showTip = roll <= TIP_PROBABILITY;

    log.debug("Tip probability check", {
      roll: roll.toFixed(3),
      threshold: TIP_PROBABILITY,
      showTip,
    });

    return showTip;
  }, []);

  /**
   * Get the next tip content.
   * Returns null if feature disabled or no tips available.
   * Tracks seen tips to avoid repetition within session.
   */
  const getNextTip = useCallback((): string | null => {
    if (!FEATURE_FLAGS.ENABLE_LEARNING_TIPS) {
      return null;
    }

    if (availableTipsRef.current.length === 0) {
      return null;
    }

    const tip = selectTip();
    if (tip) {
      log.info("📚 Tip selected", {
        tipId: tip.id,
        content: tip.content.substring(0, 40) + "...",
      });
      return tip.content;
    }

    return null;
  }, [selectTip]);

  return {
    shouldShowTip,
    getNextTip,
  };
}
